import { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';

/**
 *
 * @param {string} externalId
 * @param {string?} prefix
 * @returns {string}
 */
export const formatPropertyId = (externalId, prefix) => {
    if (!externalId) { return ''; }
    const splitIds = externalId.split(':');
    if (splitIds?.[1]) {
        return `${prefix ?? ''}${externalId.split(':')?.[1]}`;
    } else {
        // If there's no type to the string (RexListingId:) just display the whole string
        return externalId;
    }
};

export const formatCurrency = (value, currency = 'GBP') => {
    return Number(value).toLocaleString('en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
};

/**
 * @param {number} monthlyRent
 * @param {string} currency
 * @param {import("$types/generated/cms-graphql.d.ts").PropertiesRentalPropertyDetailPagePriceRentPeriodChoices} [period]
 * @param {{ includeSuffix: boolean }} [opts]
 * @returns {string | undefined}
 */
export const formatRentPrice = (monthlyRent, currency, period, opts = { includeSuffix: true }) => {
    let periodMultiplier = 1;
    let suffix = '';
    switch (period) {
        case 'WEEKLY':
            periodMultiplier = 0.25;
            suffix = 'Week';
            break;
        case 'MONTHLY':
            suffix = 'Month';
            break;
        case 'ANNUALLY':
            periodMultiplier = periodMultiplier * 12;
            suffix = 'Year';
            break;
    }
    return `${formatCurrency(monthlyRent * periodMultiplier, currency)}${(suffix && opts?.includeSuffix) ? `/${suffix}` : ''}`;
};

export const formatPublicationDate = (isoString) => {
    if (!isoString) { return ''; }
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
};

/**
 * Group property modules into sections
 * @type {import("$types/components/utilities").GroupPropertyModulesStreamFn}
 */
export const groupPropertyModulesStream = (modules) => {
    if (modules.length > 0) {
        let results = [];
        let currentSection = -1;
        for (const mod of modules) {
            switch (mod.__typename) {
                // Section start
                case 'PropertyDetailSectionTextItem':
                    currentSection += 1;
                    results.push({
                        ...mod,
                        __typename: 'PropertyDetailSection',
                        images: []
                    });
                    break;
                // Section images
                case 'PropertyDetailSectionMediaItem':
                    if (results[currentSection]?.__typename === 'PropertyDetailSection') {
                        results[currentSection]?.images?.push(mod);
                    } else {
                        currentSection += 1;
                        results.push({
                            heading: '',
                            body: '',
                            __typename: 'PropertyDetailSection',
                            images: [mod]
                        });
                    }
                    break;
                // Different "sections"
                default:
                    currentSection += 1;
                    results.push(mod);
                    break;
            }
        }
        return results;
    }
};

/**
 * Format link block to url.
 * @param {object} data
 * @returns {string}
 */
export const formatLinkBlockToUrl = (data) => {
    const linkTo = data?.linkTo;
    let url = null;
    switch (linkTo) {
        case 'email':
            url = 'mailto:' + data[linkTo];
            break;
        case 'anchor':
            url = '#' + data[linkTo];
            break;
        case 'custom_url':
            url = data?.customUrl;
            break;
        case 'file':
            url = data[linkTo]?.url;
            break;
        case 'page':
            url = data[linkTo]?.url;
            break;
        case 'phone':
            url = 'tel:' + data[linkTo];
            break;
    }

    return url;
};

export const usePersistedState = (defaulValue, key, { sync } = { sync: true }) => {
    const [value, setValue] = useState(defaulValue);
    const [expireAt, setExpireAt] = useState(Infinity);
    const [isReady, setIsReady] = useState(false);

    const persistValue = useCallback((v, exp = Infinity) => {
        setValue(v);
        try {
            localStorage.setItem(key, JSON.stringify(v));
            localStorage.setItem(`${key}_exp`, JSON.stringify(exp));
            setExpireAt(exp);
        } catch { }
    }, [setValue, setExpireAt]);

    const clearValue = () => {
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}_exp`);
        setValue(null);
        setValue(setExpireAt);
    };


    useEffect(() => {
        try {
            const expireAt = JSON.parse(localStorage.getItem(`${key}_exp`)) || Infinity;

            if (expireAt > new Date().valueOf()) {
                const persistedValue = JSON.parse(localStorage.getItem(key));
                setValue(persistedValue);
                setExpireAt(expireAt);
            }
        }
        catch { }

        setIsReady(true);
    }, []);


    // Keep storage in sync
    useEffect(() => {
        if (sync) {
            /** @param {StorageEvent} event */
            const handleStorageUpdate = (event) => {
                if (event.key === key) {
                    const expireAt = JSON.parse(localStorage.getItem(`${key}_exp`)) || Infinity;

                    if (expireAt > new Date().valueOf()) {
                        const persistedValue = JSON.parse(localStorage.getItem(key));
                        setValue(persistedValue);
                        setExpireAt(expireAt);
                    }
                }
            };

            window.addEventListener('storage', handleStorageUpdate);
            return () => {
                window.removeEventListener('storage', handleStorageUpdate);
            };
        }
    }, [sync]);


    return {
        value,
        setValue: persistValue,
        clearValue,
        isReady,
        expireAt
    };
};

export const usePersistedCookieState = (defaulValue, key, { sync, cookieOptions } = { sync: true }) => {
    const [value, setValue] = useState(defaulValue);
    const [expireAt, setExpireAt] = useState(Infinity);
    const [isReady, setIsReady] = useState(false);

    cookieOptions = {
        path: '/',
        sameSite: 'Lax',
        ...cookieOptions
    };

    const persistValue = useCallback((v, exp = Infinity) => {
        setValue(v);
        try {
            Cookies.set(key, JSON.stringify(v), {
                // Set expiry date to avoid session cookies
                expires: dayjs().add(1, 'year').toDate(),
                ...cookieOptions
            });
            localStorage.setItem(`${key}_cookie_exp`, exp);
            setExpireAt(exp);
        } catch { }
    }, [setValue, setExpireAt]);

    const clearValue = () => {
        Cookies.remove(key, cookieOptions);
        localStorage.removeItem(`${key}_cookie_exp`);
        setValue(null);
        setValue(setExpireAt);
    };


    useEffect(() => {
        try {
            if (localStorage.getItem(`${key}_cookie_exp`) !== 'Infinity') {
                const expireAt = JSON.parse(localStorage.getItem(`${key}_cookie_exp`)) || Infinity;

                if (expireAt > new Date().valueOf()) {
                    const persistedValue = Cookies.get(key) ? JSON.parse(Cookies.get(key)) : value;
                    setValue(persistedValue);
                    setExpireAt(expireAt);
                }
            } else {
                const persistedValue = Cookies.get(key) ? JSON.parse(Cookies.get(key)) : value;
                setValue(persistedValue);
            }
        }
        catch { }

        setIsReady(true);
    }, []);


    // Keep storage in sync
    useEffect(() => {
        if (sync) {
            /** @param {StorageEvent} event */
            const handleStorageUpdate = (event) => {
                if (event.key === key) {
                    const expireAt = JSON.parse(localStorage.getItem(`${key}_cookie_exp`)) || Infinity;

                    if (expireAt > new Date().valueOf()) {
                        const persistedValue = JSON.parse(Cookies.get(key));
                        setValue(persistedValue);
                        setExpireAt(expireAt);
                    }
                }
            };

            window.addEventListener('storage', handleStorageUpdate);
            return () => {
                window.removeEventListener('storage', handleStorageUpdate);
            };
        }
    }, [sync]);


    return {
        value,
        setValue: persistValue,
        clearValue,
        isReady,
        expireAt
    };
};

/**
 * Safely get a dot-notated path within a nested object, with ability to return a default if the full key path does not exist or the value is undefined
 * @param {object} obj
 * @param {string | Array<string | number>} key dot-notated path
 * @returns
 * @example
 * //use string dot notation for keys
 * dlv(obj, 'a.b.c');
 * //or use an array key
 * dlv(obj, ['a', 'b', 'c']);
 * @link https://github.com/developit/dlv
 */
export const dlv = (obj, key, def, p, undef) => {
    key = key.split ? key.split('.') : key;
    for (p = 0; p < key.length; p++) {
        obj = obj ? obj[key[p]] : undef;
    }
    return obj === undef ? def : obj;
};
/**
 * @function groupBy
 * @param {string | Array<string | number>} key dot-notated path
 * @returns {object} an object of array group by key
 * @example
 *  groupBy('objectKey')([...])
 * @link https://gist.github.com/JamieMason/0566f8412af9fe6a1d470aa1e089a752
 */
export const groupBy = key => array =>
    array.reduce(
        (objectsByKeyValue, obj) => ({
            ...objectsByKeyValue,
            [dlv(obj, key)]: (objectsByKeyValue[dlv(obj, key)] || []).concat(obj)
        }),
        {}
    );
